import * as dater from '@/core/utils/date-helper'
import { filter, IPayload } from '@/core/utils/payload-helper'
import { User, UserPayload } from '@/core/models/user'
import { Stroke, StrokePayload } from '@/core/models/stroke'
import { WorkoutSet, WorkoutSetPayload } from '@/core/models/workout-set'
import { TrainingProgram, TrainingProgramPayload } from '@/core/models/training-program'

export interface WorkoutPayload {
    id: number
    user_id: number
    stroke_id: number
    name: string
    creator: string
    description: string
    coach_advice: string
    headline: string
    video_url: string
    photo_url: string
    associates: string[]
    resources: string[]
    attachments: string[]
    level: string
    sport_type: string
    workout_types: string[]
    workout_distance: number
    workout_distance_unit: string
    workout_duration: string
    workout_rest_time: string
    workout_total_time: string
    workout_calories_burned: number
    workout_steps: number
    swimming_location: string
    pool_lengths: number[]
    pool_25: boolean
    executions_count: number
    provider: string
    ai: boolean
    enabled: boolean
    archived: boolean
    user?: UserPayload
    stroke?: StrokePayload
    sets?: WorkoutSetPayload[]
    programs?: TrainingProgramPayload[]
    goals?: number[]
    stroke_ids?: number[]
    translations?: {}
}

export class Workout {

    id: number
    userId: number
    strokeId: number
    creator: string
    name: string
    description: string
    coachAdvice: string
    headline: string
    videoUrl: string
    photo?: File
    photoUrl: string
    associates: string[]
    resources: string[]
    resourceName: string
    resourceUrl: string
    attachments: string[]
    attachmentUps: string[]
    level: string
    sportType: string
    workoutTypes: string[]
    workoutDistance: number
    workoutDistanceUnit: string
    workoutDuration: string
    workoutRestTime: string
    workoutTotalTime: string
    workoutCaloriesBurned: number
    workoutSteps: number
    swimmingLocation: string
    poolLengths: number[]
    pool25: boolean
    executionsCount: number
    provider: string
    ai: boolean
    enabled: boolean
    archived: boolean
    user: User
    stroke: Stroke
    sets: WorkoutSet[] = <WorkoutSet[]>[]
    programs: TrainingProgram[] = <TrainingProgram[]>[]
    goals: number[]
    strokes: number[]
    translations?: {} = <any>{}

    constructor(data: WorkoutPayload) {
        this.id = data.id
        this.userId = data.user_id
        this.strokeId = data.stroke_id
        this.creator = data.creator || 'openswim'
        this.name = data.name
        this.description = data.description
        this.coachAdvice = data.coach_advice
        this.headline = data.headline
        this.videoUrl = data.video_url
        this.photoUrl = data.photo_url
        this.associates = data.associates
        this.resources = data.resources
        this.resourceName = data.resources ? data.resources[0]?.name : ''
        this.resourceUrl = data.resources ? data.resources[0]?.url : ''
        this.attachments = data.attachments
        this.level = data.level
        this.sportType = data.sport_type
        this.workoutTypes = data.workout_types
        this.workoutDistance = data.workout_distance
        this.workoutDistanceUnit = data.workout_distance_unit
        this.workoutDuration = data.workout_duration
        this.workoutRestTime = data.workout_rest_time
        this.workoutTotalTime = data.workout_total_time
        this.workoutCaloriesBurned = data.workout_calories_burned
        this.workoutSteps = data.workout_steps
        this.swimmingLocation = data.swimming_location
        this.poolLengths = data.pool_lengths
        this.pool25 = data.pool_25
        this.executionsCount = data.executions_count
        this.provider = data.provider
        this.ai = data.ai
        this.enabled = data.enabled
        this.archived = data.archived
        this.user = new User(data.user || <UserPayload>{})
        this.stroke = new Stroke(data.stroke || <StrokePayload>{})
        this.capture(data)
    }

    fill(data: WorkoutPayload): this {
        this.id = data.id
        this.userId = data.user_id
        this.strokeId = data.stroke_id
        this.creator = data.creator || 'openswim'
        this.name = data.name
        this.description = data.description
        this.coachAdvice = data.coach_advice
        this.headline = data.headline
        this.videoUrl = data.video_url
        this.photoUrl = data.photo_url
        this.associates = data.associates
        this.resources = data.resources
        this.resourceName = data.resources ? data.resources[0]?.name : ''
        this.resourceUrl = data.resources ? data.resources[0]?.url : ''
        this.attachments = data.attachments
        this.level = data.level
        this.sportType = data.sport_type
        this.workoutTypes = data.workout_types
        this.workoutDistance = data.workout_distance
        this.workoutDistanceUnit = data.workout_distance_unit
        this.workoutDuration = data.workout_duration
        this.workoutRestTime = data.workout_rest_time
        this.workoutTotalTime = data.workout_total_time
        this.workoutCaloriesBurned = data.workout_calories_burned
        this.workoutSteps = data.workout_steps
        this.swimmingLocation = data.swimming_location
        this.poolLengths = data.pool_lengths
        this.pool25 = data.pool_25
        this.executionsCount = data.executions_count
        this.provider = data.provider
        this.ai = data.ai
        this.enabled = data.enabled
        this.archived = data.archived
        this.user = new User(data.user || <UserPayload>{})
        this.stroke = new Stroke(data.stroke || <StrokePayload>{})
        this.capture(data)
        return this
    }

    capture(data: WorkoutPayload): this {
        if (data.sets) {
            this.sets = <WorkoutSet[]>[]
            data.sets.map((item) =>  this.sets.push(new WorkoutSet(item)))
        }
        if (data.programs) {
            this.programs = <TrainingProgram[]>[]
            data.programs.map((item) => this.programs.push(new TrainingProgram(item)))
        }
        if (data.stroke_ids) {
            this.strokes = data.stroke_ids
        }
        if (data.goals) {
            this.goals = data.goals
        }
        if (data.translations) {
            this.translations = data.translations
        }
        return this
    }

    addSet(): this {
        this.sets.push(new WorkoutSet(<WorkoutSetPayload>{}))
        return this
    }

    delSet(number: number): this {
        const sets = this.sets.map(item => JSON.parse(JSON.stringify(item.payload())))
        sets.splice(number, 1)
        this.sets = []
        sets.map(item => this.sets.push(new WorkoutSet(item)))
        return this
    }

    duplicateSet(number: number): this {
        const set = JSON.parse(JSON.stringify(this.sets[number].payload()))
        set.id = undefined
        this.sets.push(new WorkoutSet(set))
        return this
    }

    updateSetNumber(currentNumber: number, number: number): this {
        const set = JSON.parse(JSON.stringify(this.sets[currentNumber].payload()))
        this.sets.splice(currentNumber, 1)
        const sets = this.sets.map((item: WorkoutSet) => JSON.parse(JSON.stringify(item.payload())))
        sets.splice(number, 0, set)
        this.sets = []
        sets.forEach((item: WorkoutSetPayload) => {
            this.sets.push(new WorkoutSet(item))
        })
        return this
    }

    delAttachment(number: number): this {
        const attachments = this.attachments.map(i => JSON.parse(JSON.stringify(i)))
        attachments.splice(number, 1)
        this.attachments = []
        attachments.map(i => this.attachments.push(i))
        return this
    }

    updateAttachmentNumber(currentNumber: number, number: number): this {
        const set = JSON.parse(JSON.stringify(this.attachments[currentNumber]))
        this.attachments.splice(currentNumber, 1)
        const attachments = this.attachments.map(i => JSON.parse(JSON.stringify(i)))
        attachments.splice(number, 0, set)
        this.attachments = []
        attachments.map(i => this.attachments.push(i))
        return this
    }

    public get distance(): number {
        let sum = 0
        this.sets.forEach((set: WorkoutSet) => {
            let distance = parseInt(<any>(set.distance || 0))
            if (distance) {
                sum = sum + distance * parseInt(<any>(set.repetitionNumbers || 1))
            }
        })
        return sum
    }

    public get duration(): string {
        let ms = 0
        this.sets.forEach((set: WorkoutSet) => {
            if (set._duration() !== '00:00:00.000') {
                ms = ms + dater.timeStringToMilliseconds(set._duration()) * (set.repetitionNumbers || 1)
            }
        })
        if (ms) {
            return dater.millisecondsToTimeString(ms, true)
        } else {
            return '00:00:00'
        }
    }

    public get totalTime(): string {
        let ms = 0
        this.sets.forEach((set: WorkoutSet) => {
            if (set._duration() !== '00:00:00.000') {
                ms = ms + (dater.timeStringToMilliseconds(set._duration()) + dater.timeStringToMilliseconds(set._restTime())) * (set.repetitionNumbers || 1) + dater.timeStringToMilliseconds(set._setRestTime())
            }
        })
        if (ms) {
            return dater.millisecondsToTimeString(ms, true)
        } else {
            return '00:00:00'
        }
    }

    setsPayload(): WorkoutSetPayload[] {
        const sets = <WorkoutSetPayload[]>[]
        this.sets.forEach((item: WorkoutSet) => {
            sets.push(item.payload())
        })
        return sets
    }

    resourcesPayload(): [] {
        const resources = []
        if (this.resourceName || this.resourceUrl) {
            let resource = <{ name: string, url: string }>{}
            if (this.resourceName) {
                resource.name = this.resourceName
            }
            if (this.resourceUrl) {
                resource.url = this.resourceUrl
            }
            resources.push(resource)
        }
        return resources
    }

    exists(): boolean {
        return !! this.id
    }

    unset(): this {
        this.fill(<WorkoutPayload>{})
        this.photo = undefined
        this.attachmentUps = undefined
        this.resourceName = undefined
        this.resourceUrl = undefined
        this.sets = <WorkoutSet[]>[]
        this.goals = <number[]>[]
        this.strokes = <number[]>[]
        this.translations = {}
        return this
    }

    payload(fillable: string[] = []): WorkoutPayload | IPayload {
        let payload = {
            id: this.id,
            user_id: this.userId,
            stroke_id: this.strokeId,
            creator: this.creator,
            name: this.name,
            description: this.description,
            coach_advice: this.coachAdvice,
            headline: this.headline,
            video_url: this.videoUrl,
            associates: this.associates || [],
            resources: this.resourcesPayload(),
            attachments: this.attachments || [],
            level: this.level,
            sport_type: this.sportType,
            workout_types: this.workoutTypes,
            swimming_location: this.swimmingLocation,
            pool_25: this.pool25,
            ai: this.ai,
            enabled: this.enabled,
            archived: this.archived,
            sets: this.setsPayload(),
            goals: this.goals,
            strokes: this.strokes,
            translations: this.translations,
        }
        return filter(payload, fillable)
    }
}
